export type HandoffStatus = 'completed' | 'partial' | 'blocked' | 'failed' | 'in_progress';
export type TestStatus = 'passed' | 'failed' | 'running' | 'skipped' | 'unknown';

export const HANDOFF_LIMITS = {
  textField: 500,
  shortField: 180,
  files: 20,
  tests: 20,
  blockers: 10,
  remainingWork: 10,
  references: 12,
  ownerAttention: 8,
  factsMissing: 16,
  humanText: 1800,
} as const;

export interface HandoffWorkerInput {
  readonly id?: string | null;
  readonly number?: number | null;
  readonly name?: string | null;
}

export interface HandoffTaskInput {
  readonly id?: string | null;
  readonly title?: string | null;
}

export interface HandoffRunInput {
  readonly state?: 'queued' | 'starting' | 'running' | 'completed' | 'failed' | 'stopped' | 'cancelled' | 'unknown' | null;
  readonly success?: boolean | null;
  readonly startedAt?: string | null;
  readonly endedAt?: string | null;
  readonly summary?: string | null;
  readonly unresolvedRequiredWork?: readonly string[] | null;
}

export interface HandoffTestInput {
  readonly name?: string | null;
  readonly command?: string | null;
  readonly status: TestStatus;
  readonly detail?: string | null;
}

export interface HandoffBlockerInput {
  readonly summary: string;
  readonly resolved?: boolean | null;
  readonly requiresOwnerDecision?: boolean | null;
}

export interface HandoffApprovalInput {
  readonly waiting: boolean;
  readonly summary?: string | null;
}

export interface HandoffArtifactInput {
  readonly label: string;
  readonly reference: string;
  readonly url?: string | null;
}

export interface AutomaticHandoffInput {
  readonly worker?: HandoffWorkerInput | null;
  readonly project?: string | null;
  readonly task?: HandoffTaskInput | null;
  readonly goal?: string | null;
  readonly run?: HandoffRunInput | null;
  readonly runtime?: string | null;
  readonly model?: string | null;
  readonly filesChanged?: readonly string[] | null;
  readonly tests?: readonly HandoffTestInput[] | null;
  readonly commitSha?: string | null;
  readonly pullRequest?: { readonly number?: number | null; readonly url?: string | null } | null;
  readonly artifacts?: readonly HandoffArtifactInput[] | null;
  readonly blockers?: readonly HandoffBlockerInput[] | null;
  readonly approval?: HandoffApprovalInput | null;
  readonly sessionLifecycle?: string | null;
  readonly previousHandoffRef?: string | null;
  /** Untyped evidence is deliberately not copied into Phase 1 output. */
  readonly genericEvidence?: Readonly<Record<string, unknown>> | null;
}

export interface ComposedWorker {
  readonly id: string | null;
  readonly number: number | null;
  readonly name: string | null;
  readonly label: string;
}

export interface ComposedTask {
  readonly id: string | null;
  readonly title: string | null;
}

export interface ComposedTest {
  readonly name: string | null;
  readonly command: string | null;
  readonly status: TestStatus;
  readonly detail: string | null;
}

export interface HandoffReference {
  readonly kind: 'commit' | 'pull_request' | 'artifact' | 'previous_handoff';
  readonly label: string;
  readonly reference: string;
  readonly url: string | null;
}

export interface AutomaticHandoff {
  readonly headline: string;
  readonly status: HandoffStatus;
  readonly worker: ComposedWorker | null;
  readonly project: string | null;
  readonly task: ComposedTask | null;
  readonly goal: string | null;
  readonly result: string | null;
  /** null means unknown; [] means known to be none. */
  readonly filesChanged: readonly string[] | null;
  /** null means unknown; [] means known to be none. */
  readonly tests: readonly ComposedTest[] | null;
  readonly codeReferences: readonly HandoffReference[];
  /** null means blocker state was not supplied; [] means known to be clear. */
  readonly blockers: readonly string[] | null;
  readonly remainingWork: readonly string[];
  readonly ownerAttention: readonly string[];
  readonly recommendedNextStep: string;
  readonly factsMissing: readonly string[];
  readonly runtime: string | null;
  readonly model: string | null;
  readonly startedAt: string | null;
  readonly endedAt: string | null;
  readonly sessionLifecycle: string | null;
}

function clip(value: string | null | undefined, limit: number): string | null {
  if (value == null) return null;
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (normalized === '') return null;
  return normalized.length <= limit ? normalized : `${normalized.slice(0, Math.max(0, limit - 1))}…`;
}

function dedupeStrings(values: readonly string[], limit: number): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of values) {
    const value = clip(raw, HANDOFF_LIMITS.textField);
    if (value == null || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
    if (result.length >= limit) break;
  }
  return result;
}

function composeWorker(input: HandoffWorkerInput | null | undefined): ComposedWorker | null {
  if (input == null) return null;
  const id = clip(input.id, HANDOFF_LIMITS.shortField);
  const name = clip(input.name, HANDOFF_LIMITS.shortField);
  const number = Number.isInteger(input.number) && (input.number ?? 0) >= 0 ? (input.number ?? null) : null;
  let label: string | null = null;
  if (number != null && name != null) label = `Worker ${number} (${name})`;
  else if (number != null) label = `Worker ${number}`;
  else if (name != null) label = name;
  else if (id != null) label = id;
  if (label == null) return null;
  return { id, number, name, label };
}

function composeTask(input: HandoffTaskInput | null | undefined): ComposedTask | null {
  if (input == null) return null;
  const id = clip(input.id, HANDOFF_LIMITS.shortField);
  const title = clip(input.title, HANDOFF_LIMITS.textField);
  return id == null && title == null ? null : { id, title };
}

function composeTests(input: readonly HandoffTestInput[] | null | undefined): ComposedTest[] | null {
  if (input == null) return null;
  const seen = new Set<string>();
  const result: ComposedTest[] = [];
  for (const test of input) {
    const name = clip(test.name, HANDOFF_LIMITS.shortField);
    const command = clip(test.command, HANDOFF_LIMITS.textField);
    const detail = clip(test.detail, HANDOFF_LIMITS.textField);
    const key = JSON.stringify([name, command, test.status, detail]);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ name, command, status: test.status, detail });
    if (result.length >= HANDOFF_LIMITS.tests) break;
  }
  return result;
}

function unresolvedBlockers(input: readonly HandoffBlockerInput[] | null | undefined): HandoffBlockerInput[] {
  if (input == null) return [];
  const seen = new Set<string>();
  const result: HandoffBlockerInput[] = [];
  for (const blocker of input) {
    if (blocker.resolved === true) continue;
    const summary = clip(blocker.summary, HANDOFF_LIMITS.textField);
    if (summary == null || seen.has(summary)) continue;
    seen.add(summary);
    result.push({
      summary,
      resolved: false,
      requiresOwnerDecision: blocker.requiresOwnerDecision === true,
    });
    if (result.length >= HANDOFF_LIMITS.blockers) break;
  }
  return result;
}

function safeUrl(value: string | null | undefined): string | null {
  const clipped = clip(value, HANDOFF_LIMITS.textField);
  if (clipped == null) return null;
  try {
    const parsed = new URL(clipped);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    parsed.search = '';
    parsed.hash = '';
    return clip(parsed.toString(), HANDOFF_LIMITS.textField);
  } catch {
    return null;
  }
}

function composeReferences(input: AutomaticHandoffInput): HandoffReference[] {
  const refs: HandoffReference[] = [];
  const seen = new Set<string>();
  const add = (ref: HandoffReference): void => {
    const key = `${ref.kind}\u0000${ref.reference}`;
    if (seen.has(key) || refs.length >= HANDOFF_LIMITS.references) return;
    seen.add(key);
    refs.push(ref);
  };

  const commit = clip(input.commitSha, HANDOFF_LIMITS.shortField);
  if (commit != null) add({ kind: 'commit', label: 'Commit', reference: commit, url: null });

  const prNumber = Number.isInteger(input.pullRequest?.number) && (input.pullRequest?.number ?? 0) > 0
    ? (input.pullRequest?.number ?? null)
    : null;
  const prUrl = safeUrl(input.pullRequest?.url);
  if (prNumber != null || prUrl != null) {
    add({
      kind: 'pull_request',
      label: prNumber == null ? 'Pull request' : `PR #${prNumber}`,
      reference: prNumber == null ? (prUrl ?? 'pull-request') : `#${prNumber}`,
      url: prUrl,
    });
  }

  for (const artifact of input.artifacts ?? []) {
    const label = clip(artifact.label, HANDOFF_LIMITS.shortField);
    const reference = clip(artifact.reference, HANDOFF_LIMITS.textField);
    if (label == null || reference == null) continue;
    add({ kind: 'artifact', label, reference, url: safeUrl(artifact.url) });
  }

  const previous = clip(input.previousHandoffRef, HANDOFF_LIMITS.textField);
  if (previous != null) add({ kind: 'previous_handoff', label: 'Previous handoff', reference: previous, url: null });
  return refs;
}

function hasFailedTest(tests: readonly ComposedTest[] | null): boolean {
  return tests?.some((test) => test.status === 'failed') ?? false;
}

function hasUnresolvedRequiredWork(run: HandoffRunInput | null | undefined): boolean {
  if (run?.unresolvedRequiredWork == null) return false;
  return dedupeStrings(run.unresolvedRequiredWork, HANDOFF_LIMITS.factsMissing).length > 0;
}

/**
 * Status precedence is intentionally deterministic:
 * 1. an explicitly failed run is failed;
 * 2. pending approval or any unresolved blocker makes it blocked;
 * 3. a queued/starting/running run is in_progress;
 * 4. a completed run with unresolved required work or a reported failed test is partial;
 * 5. a completed/successful run is completed;
 * 6. otherwise the available facts are incomplete, so the result is partial rather than guessed complete.
 */
function deriveStatus(input: AutomaticHandoffInput, tests: readonly ComposedTest[] | null, blockers: readonly HandoffBlockerInput[]): HandoffStatus {
  if (input.run?.state === 'failed' || input.run?.success === false) return 'failed';
  if (input.approval?.waiting === true || blockers.length > 0) return 'blocked';
  if (input.run?.state === 'queued' || input.run?.state === 'starting' || input.run?.state === 'running') return 'in_progress';
  if (hasUnresolvedRequiredWork(input.run) || hasFailedTest(tests)) return 'partial';
  if (input.run?.state === 'completed' || input.run?.success === true) return 'completed';
  return 'partial';
}

function deriveOwnerAttention(input: AutomaticHandoffInput, status: HandoffStatus, blockers: readonly HandoffBlockerInput[]): string[] {
  const attention: string[] = [];
  const add = (value: string): void => {
    if (!attention.includes(value) && attention.length < HANDOFF_LIMITS.ownerAttention) attention.push(value);
  };
  if (input.approval?.waiting === true) {
    const detail = clip(input.approval.summary, HANDOFF_LIMITS.textField);
    add(detail == null ? 'Approval is waiting for the owner.' : `Approval is waiting for the owner: ${detail}`);
  }
  for (const blocker of blockers) {
    if (blocker.requiresOwnerDecision === true) add(`Owner decision needed: ${blocker.summary}`);
  }
  if (status === 'failed') add('The work failed; the owner must decide whether it should be retried.');
  return attention;
}

function pullRequestLabel(references: readonly HandoffReference[]): string | null {
  return references.find((ref) => ref.kind === 'pull_request')?.label ?? null;
}

function deriveNextStep(
  input: AutomaticHandoffInput,
  status: HandoffStatus,
  tests: readonly ComposedTest[] | null,
  blockers: readonly HandoffBlockerInput[],
  references: readonly HandoffReference[],
): string {
  if (input.approval?.waiting === true) return 'Provide the requested approval.';
  if (blockers.some((blocker) => blocker.requiresOwnerDecision === true)) return 'Resolve the blocker that requires an owner decision.';
  if (blockers.length > 0) return 'Retry after the blocker is resolved.';
  if (tests?.some((test) => test.status === 'failed')) return 'Inspect the failed test.';
  if (status === 'failed') return 'Decide whether to retry the failed work.';
  const pr = pullRequestLabel(references);
  if ((status === 'completed' || status === 'partial') && pr != null) return `Review ${pr}.`;
  if (status === 'partial' && hasUnresolvedRequiredWork(input.run)) return 'Review the remaining required work.';
  return 'No next action known.';
}

function deriveFactsMissing(input: AutomaticHandoffInput): string[] {
  const missing: string[] = [];
  const add = (fact: string): void => {
    if (!missing.includes(fact) && missing.length < HANDOFF_LIMITS.factsMissing) missing.push(fact);
  };
  if (composeWorker(input.worker) == null) add('worker');
  if (clip(input.project, HANDOFF_LIMITS.shortField) == null) add('project');
  if (composeTask(input.task) == null) add('task');
  if (clip(input.goal, HANDOFF_LIMITS.textField) == null) add('goal');
  if (input.run?.state == null && input.run?.success == null) add('runStatus');
  if (clip(input.run?.summary, HANDOFF_LIMITS.textField) == null) add('result');
  if (input.filesChanged == null) add('filesChanged');
  if (input.tests == null) add('tests');
  if (input.blockers == null) add('blockers');
  return missing;
}

function statusVerb(status: HandoffStatus): string {
  switch (status) {
    case 'completed': return 'completed';
    case 'partial': return 'partially completed';
    case 'blocked': return 'is blocked on';
    case 'failed': return 'failed';
    case 'in_progress': return 'is working on';
  }
}

function deriveHeadline(status: HandoffStatus, worker: ComposedWorker | null, task: ComposedTask | null): string {
  const who = worker?.label ?? 'Worker';
  const title = task?.title;
  if (title != null) return clip(`${who} ${statusVerb(status)} “${title}”`, HANDOFF_LIMITS.textField) ?? `${who} ${status}`;
  return clip(`${who}: ${status.replace('_', ' ')}`, HANDOFF_LIMITS.textField) ?? status;
}

export function composeAutomaticHandoff(input: AutomaticHandoffInput): AutomaticHandoff {
  const worker = composeWorker(input.worker);
  const task = composeTask(input.task);
  const tests = composeTests(input.tests);
  const blockers = unresolvedBlockers(input.blockers);
  const status = deriveStatus(input, tests, blockers);
  const references = composeReferences(input);
  const project = clip(input.project, HANDOFF_LIMITS.shortField);
  const goal = clip(input.goal, HANDOFF_LIMITS.textField);
  const result = clip(input.run?.summary, HANDOFF_LIMITS.textField);
  const filesChanged = input.filesChanged == null ? null : dedupeStrings(input.filesChanged, HANDOFF_LIMITS.files);
  const remainingWork = input.run?.unresolvedRequiredWork == null
    ? []
    : dedupeStrings(input.run.unresolvedRequiredWork, HANDOFF_LIMITS.remainingWork);
  return {
    headline: deriveHeadline(status, worker, task),
    status,
    worker,
    project,
    task,
    goal,
    result,
    filesChanged,
    tests,
    codeReferences: references,
    blockers: input.blockers == null ? null : blockers.map((blocker) => blocker.summary),
    remainingWork,
    ownerAttention: deriveOwnerAttention(input, status, blockers),
    recommendedNextStep: deriveNextStep(input, status, tests, blockers, references),
    factsMissing: deriveFactsMissing(input),
    runtime: clip(input.runtime, HANDOFF_LIMITS.shortField),
    model: clip(input.model, HANDOFF_LIMITS.shortField),
    startedAt: clip(input.run?.startedAt, HANDOFF_LIMITS.shortField),
    endedAt: clip(input.run?.endedAt, HANDOFF_LIMITS.shortField),
    sessionLifecycle: clip(input.sessionLifecycle, HANDOFF_LIMITS.shortField),
  };
}

function testPhrase(test: ComposedTest): string {
  const name = test.name ?? test.command ?? 'Test';
  return `${name}: ${test.status}`;
}

function referencePhrase(ref: HandoffReference): string {
  return ref.kind === 'artifact' ? `${ref.label}: ${ref.reference}` : ref.label;
}

/** Generates text strictly from an already-composed structured handoff. */
export function renderAutomaticHandoff(handoff: AutomaticHandoff): string {
  const parts: string[] = [`${handoff.headline}.`];
  if (handoff.goal != null) parts.push(`Goal: ${handoff.goal}.`);
  if (handoff.result != null) parts.push(`Result: ${handoff.result}.`);

  if (handoff.filesChanged == null) parts.push('Files changed: unknown.');
  else if (handoff.filesChanged.length === 0) parts.push('Files changed: none reported.');
  else parts.push(`Files changed (${handoff.filesChanged.length}): ${handoff.filesChanged.join(', ')}.`);

  if (handoff.tests == null) parts.push('Tests: unknown.');
  else if (handoff.tests.length === 0) parts.push('Tests: none reported.');
  else parts.push(`Tests: ${handoff.tests.map(testPhrase).join('; ')}.`);

  if (handoff.codeReferences.length > 0) parts.push(`References: ${handoff.codeReferences.map(referencePhrase).join('; ')}.`);
  if (handoff.blockers == null) parts.push('Blockers: unknown.');
  else if (handoff.blockers.length === 0) parts.push('Blockers: none reported.');
  else parts.push(`Blockers: ${handoff.blockers.join('; ')}.`);
  if (handoff.remainingWork.length > 0) parts.push(`Remaining work: ${handoff.remainingWork.join('; ')}.`);
  if (handoff.ownerAttention.length > 0) parts.push(`Owner attention: ${handoff.ownerAttention.join('; ')}.`);
  parts.push(`Next step: ${handoff.recommendedNextStep}`);
  if (handoff.factsMissing.length > 0) parts.push(`Missing facts: ${handoff.factsMissing.join(', ')}.`);
  return clip(parts.join(' '), HANDOFF_LIMITS.humanText) ?? '';
}
